import { Component, EventEmitter, Output, Input } from "@angular/core";
import { v4 as uuid } from "uuid";

// const MAX_FILE_SIZE = 5 * 1024 * 1024;

export interface SelectFileEvent {
  base64: string;
  type: string;
  fileId: string;
  fileName: string;
  extension: string;
}

export interface UnselectFileEvent {
  fileId: string;
  index: number;
}

interface FileUpload {
  fileName: string;
  fileType: string;
  fileData: string;
  fileId: string;
  fileSize: number;
}

@Component({
  selector: "app-upload-multiple-file",
  templateUrl: "./upload-multiple-file.component.html",
  styleUrls: ["./upload-multiple-file.component.scss"],
})
export class UploadMultipleFileComponent {
  @Input() isExhibition = false;
  @Input() preview = false;
  @Input() allowedExtensions = ["png", "jpeg", "jpg", "pdf"];
  @Input() dirtyLabel: string | undefined;
  @Input() fileSizeLimit = Infinity;
  @Input() totalFileSizeLimit = Infinity;
  @Input() maxFiles: 10; // Máximo de arquivos, indefinido se não for configurado
  @Output() selectFile = new EventEmitter<SelectFileEvent | null>();
  @Output() unselectFile = new EventEmitter<UnselectFileEvent | null>();

  showInvalidFileFormat = false;
  showFileSizeExceededError = false;
  showTotalFileSizeExceededError = false;
  showExceededFileLimit = false;
  showPreviewModal = false;

  dragged = false;
  lastFileSelected: File | null = null;
  previewFile: FileUpload | null = null;
  uploadedFiles: FileUpload[] = [];

  get hideAddFileButton() {
    return this.isExhibition || this.uploadedFiles.length === this.maxFiles;
  }

  get allowedExtensionsView() {
    return this.allowedExtensions?.reduce(
      (prev, curr) => prev + " | " + curr,
      ""
    );
  }

  get totalFileSize() {
    return this.uploadedFiles.map((x) => x.fileSize).reduce((a, b) => a + b, 0);
  }

  openPreviewModal(file: FileUpload) {
    if (!this.preview) return;

    this.showPreviewModal = true;
    this.previewFile = file;
  }

  closePreviewModal() {
    if (!this.preview) return;

    this.showPreviewModal = false;
    this.previewFile = null;
  }

  private async createFileUpload(file: File): Promise<FileUpload> {
    const base64 = await this.convertToBase64(file).then(
      (x) => x.split(",")[1]
    );

    const id = uuid();

    return {
      fileData: base64,
      fileId: id,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    };
  }

  private addFileUpload(file: FileUpload) {
    this.uploadedFiles.push(file);

    const extension =
      file.fileName.lastIndexOf(".") === -1
        ? null
        : file.fileName.substring(file.fileName.lastIndexOf(".") + 1);

    this.selectFile.emit({
      base64: file.fileData,
      type: file.fileType,
      fileName: file.fileName,
      fileId: file.fileId,
      extension: extension,
    });
  }

  removeUploadFile(file: FileUpload, index: number) {
    this.uploadedFiles = this.uploadedFiles.filter(
      (x) => x.fileId !== file.fileId
    );

    this.unselectFile.emit({ fileId: file.fileId, index });

    this.showTotalFileSizeExceededError =
      this.totalFileSize > this.totalFileSizeLimit;
  }

  handleFileSelect(event: any): void {
    const files = Array.from<File>(event.target.files);

    if (!files || !files.length) return;

    this.showInvalidFileFormat = false;
    this.showFileSizeExceededError = false;
    this.showTotalFileSizeExceededError = false;
    this.showExceededFileLimit = false;
    this.showPreviewModal = false;

    let totalLoadFileSize = this.totalFileSize;
    let fileCount = this.uploadedFiles.length;

    for (const file of files) {
      this.lastFileSelected = file;
      totalLoadFileSize += file.size;
      fileCount++;

      if (fileCount > this.maxFiles) {
        this.showExceededFileLimit = true;
        break;
      }

      if (file.size > this.fileSizeLimit) {
        this.showFileSizeExceededError = true;
        break;
      }

      if (
        this.allowedExtensions.length > 0 &&
        !this.allowedExtensions.includes(file.type.split("/")[1])
      ) {
        this.showInvalidFileFormat = true;
        break;
      }

      // apenas exibi o erro e aguarda o usuario remover arquivos para remover a mensagem
      this.showTotalFileSizeExceededError =
        totalLoadFileSize > this.totalFileSizeLimit;

      this.createFileUpload(file).then((x) => this.addFileUpload(x));
    }

    event.target.value = "";
  }

  download(document: any) {
    window.location.href = document.path;
  }

  convertToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }
}
